/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging */

import { css, CSSResultGroup, html, LitElement, nothing, PropertyValues, unsafeCSS } from "lit";
import { property } from "lit/decorators.js";

import { customElement, DesignToken, registerStyleSheet, Role, waitForUpdate } from "@m3e/web/core";

import "@m3e/web/button";
import "@m3e/web/icon-button";

/**
 * Presents short updates about application processes at the bottom of the screen.
 * @tag m3e-snackbar
 *
 * @slot - Renders the content of the snackbar.
 * @slot close-icon - Renders the icon of the button used to close the snackbar.
 *
 * @attr action - The label of the snackbar's action.
 * @attr close-label - The accessible label given to the button used to dismiss the snackbar.
 * @attr dismissible - Whether a button is presented that can be used to close the snackbar.
 * @attr duration - The length of time, in milliseconds, to wait before automatically dismissing the snackbar.
 * @attr open - Whether the snackbar is open.
 *
 * @fires beforetoggle - Dispatched before the toggle state changes.
 * @fires toggle - Dispatched after the toggle state has changed.
 *
 * @cssprop --m3e-snackbar-margin - Vertical offset from the bottom of the viewport.
 * @cssprop --m3e-snackbar-container-shape - Border radius of the snackbar container.
 * @cssprop --m3e-snackbar-container-color - Background color of the snackbar.
 * @cssprop --m3e-snackbar-padding - Internal spacing of the snackbar container.
 * @cssprop --m3e-snackbar-min-width - Minimum width of the snackbar.
 * @cssprop --m3e-snackbar-max-width - Maximum width of the snackbar.
 */
@customElement("m3e-snackbar")
export class M3eSnackbarElement extends Role(LitElement, "status") {
  static {
    registerStyleSheet(css`
      m3e-snackbar {
        margin-inline: auto;
      }
    `);
  }
  /** The styles of the element. */
  static override styles: CSSResultGroup = css`
    :host {
      contain: layout style paint;
      position: fixed;
      top: calc(100vh - var(--_snackbar-height, 0px) - var(--m3e-snackbar-margin, ${DesignToken.measurement.space200}));
      display: inline-flex;
      align-items: center;
      min-width: var(--m3e-snackbar-min-width, 344px);
      max-width: var(--m3e-snackbar-max-width, 672px);
      visibility: hidden;
      border: none;
      margin: 0;
      padding: 0;
      opacity: 0;
      transform: scaleY(0.8);
      transform-origin: bottom;
      transition: ${unsafeCSS(
        `opacity ${DesignToken.motion.duration.short3} ${DesignToken.motion.easing.standard}, 
        transform ${DesignToken.motion.duration.short3} ${DesignToken.motion.easing.standard}, 
        overlay ${DesignToken.motion.duration.short3} ${DesignToken.motion.easing.standard} allow-discrete,
        visibility ${DesignToken.motion.duration.short3} ${DesignToken.motion.easing.standard} allow-discrete`,
      )};
    }
    :host([hidden]) {
      display: none;
    }
    :host::backdrop {
      background-color: transparent;
    }
    :host(:popover-open) {
      visibility: visible;
      opacity: 1;
      transform: scaleY(1);
    }
    @starting-style {
      :host(:popover-open) {
        opacity: 0;
        transform: scaleY(0.8);
      }
    }
    .base {
      display: inline-flex;
      align-items: center;
      flex: 1 1 auto;
      box-sizing: border-box;
      padding: var(--m3e-snackbar-padding, 0 ${DesignToken.measurement.space200} 0 ${DesignToken.measurement.space200});
      border-radius: var(--m3e-snackbar-container-shape, ${DesignToken.shape.corner.extraSmall});
      background-color: var(--m3e-snackbar-container-color, ${DesignToken.color.inverseSurface});
      font-size: var(--m3e-snackbar-supporting-text-font-size, ${DesignToken.typescale.standard.label.large.fontSize});
      font-weight: var(
        --m3e-snackbar-supporting-text-font-weight,
        ${DesignToken.typescale.standard.label.large.fontWeight}
      );
      line-height: var(
        --m3e-snackbar-supporting-text-line-height,
        ${DesignToken.typescale.standard.label.large.lineHeight}
      );
      letter-spacing: var(
        --m3e-snackbar-supporting-text-tracking,
        ${DesignToken.typescale.standard.label.large.tracking}
      );
      color: var(--m3e-snackbar-supporting-text-color, ${DesignToken.color.inverseOnSurface});

      --m3e-text-button-label-text-color: var(--m3e-snackbar-action-text-color, ${DesignToken.color.inversePrimary});
      --m3e-text-button-hover-label-text-color: var(
        --m3e-snackbar-action-text-color,
        ${DesignToken.color.inversePrimary}
      );
      --m3e-text-button-hover-state-layer-color: var(
        --m3e-snackbar-action-text-color,
        ${DesignToken.color.inversePrimary}
      );
      --m3e-text-button-focus-label-text-color: var(
        --m3e-snackbar-action-text-color,
        ${DesignToken.color.inversePrimary}
      );
      --m3e-text-button-focus-state-layer-color: var(
        --m3e-snackbar-action-text-color,
        ${DesignToken.color.inversePrimary}
      );
      --m3e-text-button-pressed-label-text-color: var(
        --m3e-snackbar-action-text-color,
        ${DesignToken.color.inversePrimary}
      );
      --m3e-text-button-pressed-state-layer-color: var(
        --m3e-snackbar-action-text-color,
        ${DesignToken.color.inversePrimary}
      );
      --m3e-standard-icon-button-icon-color: var(
        --m3e-snackbar-close-icon-color,
        ${DesignToken.color.inverseOnSurface}
      );
      --m3e-standard-icon-button-hover-icon-color: var(
        --m3e-snackbar-close-icon-color,
        ${DesignToken.color.inverseOnSurface}
      );
      --m3e-standard-icon-button-hover-state-layer-color: var(
        --m3e-snackbar-close-icon-color,
        ${DesignToken.color.inverseOnSurface}
      );
      --m3e-standard-icon-button-focus-icon-color: var(
        --m3e-snackbar-close-icon-color,
        ${DesignToken.color.inverseOnSurface}
      );
      --m3e-standard-icon-button-focus-state-layer-color: var(
        --m3e-snackbar-close-icon-color,
        ${DesignToken.color.inverseOnSurface}
      );
      --m3e-standard-icon-button-pressed-icon-color: var(
        --m3e-snackbar-close-icon-color,
        ${DesignToken.color.inverseOnSurface}
      );
      --m3e-standard-icon-button-pressed-state-layer-color: var(
        --m3e-snackbar-close-icon-color,
        ${DesignToken.color.inverseOnSurface}
      );
    }
    :host([dismissible]) .base {
      padding-inline-end: ${DesignToken.measurement.space100};
    }
    .supporting-text {
      flex: 1 1 auto;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      line-clamp: 2;
      margin-block: var(--m3e-snackbar-supporting-text-margin-block, ${DesignToken.measurement.space175});
    }
    ::slotted([slot="close-icon"]),
    .close-icon {
      width: 1em;
      font-size: var(--m3e-icon-button-icon-size, 24px) !important;
    }
    @media (forced-colors: active) {
      :host {
        background-color: Canvas;
        color: CanvasText;
        border-radius: ${DesignToken.shape.corner.small};
        box-sizing: border-box;
        outline: 1px solid CanvasText;
      }
    }
  `;

  /** @private */ static __current: M3eSnackbarElement | null = null;

  /** @private */ #timeoutId = -1;
  /** @private */ #actionTaken = false;
  /** @private */ readonly #beforeToggleHandler = (e: ToggleEvent) => this.#handleBeforeToggle(e);

  /** The currently open snackbar. */
  static get current(): M3eSnackbarElement | null {
    return M3eSnackbarElement.__current;
  }

  /**
   * The length of time, in milliseconds, to wait before automatically dismissing the snackbar.
   * @default 3000
   */
  @property({ type: Number }) duration = 3000;

  /**
   * The label of the snackbar's action.
   * @default ""
   */
  @property() action = "";

  /**
   * Whether a button is presented that can be used to close the snackbar.
   * @default false
   */
  @property({ type: Boolean, reflect: true }) dismissible = false;

  /**
   * Whether the snackbar is open.
   * @default false
   */
  @property({ type: Boolean, reflect: true }) open = false;

  /**
   * The accessible label given to the button used to dismiss the snackbar.
   * @default "Close"
   */
  @property({ attribute: "close-label" }) closeLabel = "Close";

  /** Whether the user clicked the action. */
  get isActionTaken(): boolean {
    return this.#actionTaken;
  }

  /** @inheritdoc */
  override connectedCallback(): void {
    super.connectedCallback();

    this.addEventListener("beforetoggle", this.#beforeToggleHandler);
    this.setAttribute("popover", "manual");
    this.ariaLive = "polite";
  }

  /** @inheritdoc */
  override disconnectedCallback(): void {
    super.disconnectedCallback();

    this.removeEventListener("beforetoggle", this.#beforeToggleHandler);
  }

  /** @inheritdoc */
  protected override render() {
    return html`<div class="base">
      <span class="supporting-text"><slot></slot></span>
      ${this.#renderActionButton()} ${this.#renderCloseButton()}
    </div>`;
  }

  /** @inheritdoc */
  protected override updated(changed: PropertyValues<this>): void {
    super.updated(changed);

    if (changed.has("open")) {
      const popoverOpen = this.matches(":popover-open");
      if (this.open) {
        if (!popoverOpen) {
          this.showPopover();
        }
      } else if (popoverOpen) {
        this.hidePopover();
      }
    }
  }

  /** @inheritdoc */
  protected override firstUpdated(_changedProperties: PropertyValues): void {
    super.firstUpdated(_changedProperties);

    // After render, compute the (unscaled) height of the snackbar in order
    // to properly position it relative to the viewport.
    // top: calc(100vh - var(--_snackbar-height, 0px) - var(--m3e-snackbar-margin, 16px));

    this.style.setProperty("--_snackbar-height", `${this.getBoundingClientRect().height / 0.8}px`);
  }

  /** @private */
  #renderActionButton(): unknown {
    return !this.action ? nothing : html`<m3e-button @click=${this.#handleActionClick}>${this.action}</m3e-button>`;
  }

  /** @private */
  #renderCloseButton(): unknown {
    return !this.dismissible
      ? nothing
      : html`<m3e-icon-button aria-label="${this.closeLabel}" @click=${this.#handleDismissClick}>
          <slot name="close-icon">
            <svg class="close-icon" viewBox="0 -960 960 960" fill="currentColor">
              <path
                d="m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z"
              />
            </svg>
          </slot>
        </m3e-icon-button>`;
  }

  /** @private */
  #handleActionClick(): void {
    this.#actionTaken = true;
    this.open = false;
  }

  /** @private */
  #handleDismissClick(): void {
    this.open = false;
  }

  /** @private */
  async #handleBeforeToggle(e: ToggleEvent): Promise<void> {
    if (e.newState == "open") {
      if (M3eSnackbarElement.__current) {
        M3eSnackbarElement.__current.open = false;
        await waitForUpdate(M3eSnackbarElement.__current);
      }
      M3eSnackbarElement.__current = this;

      if (this.duration > 0) {
        this.#timeoutId = setTimeout(() => (this.open = false), this.duration);
      }
    } else {
      if (M3eSnackbarElement.__current === this) {
        M3eSnackbarElement.__current = null;
      }

      clearTimeout(this.#timeoutId);
    }
  }
}

interface M3eSnackbarElementEventMap extends HTMLElementEventMap {
  beforetoggle: ToggleEvent;
  toggle: ToggleEvent;
}

export interface M3eSnackbarElement {
  addEventListener<K extends keyof M3eSnackbarElementEventMap>(
    type: K,
    listener: (this: M3eSnackbarElement, ev: M3eSnackbarElementEventMap[K]) => void,
    options?: boolean | AddEventListenerOptions,
  ): void;

  addEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions,
  ): void;

  removeEventListener<K extends keyof M3eSnackbarElementEventMap>(
    type: K,
    listener: (this: M3eSnackbarElement, ev: M3eSnackbarElementEventMap[K]) => void,
    options?: boolean | EventListenerOptions,
  ): void;

  removeEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | EventListenerOptions,
  ): void;
}

declare global {
  interface HTMLElementTagNameMap {
    "m3e-snackbar": M3eSnackbarElement;
  }
}
